/**
 * Simple in-app logger for debugging on mobile devices without a Mac.
 */

type LogLevel = 'log' | 'warn' | 'error' | 'debug';

interface LogEntry {
    level: LogLevel;
    message: string;
    timestamp: number;
}

const MAX_LOGS = 200;
let logs: LogEntry[] = [];
let listeners: ((logs: LogEntry[]) => void)[] = [];

const originalLog = console.log;
const originalWarn = console.warn;
const originalError = console.error;

const addLog = (level: LogLevel, ...args: any[]) => {
    const message = args.map(arg => {
        if (arg instanceof Error) {
            return `${arg.name}: ${arg.message}\n${arg.stack}`;
        }
        return typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg);
    }).join(' ');

    const entry: LogEntry = {
        level,
        message,
        timestamp: Date.now()
    };

    logs = [entry, ...logs].slice(0, MAX_LOGS);
    listeners.forEach(listener => listener(logs));
};

// Intercept console methods
console.log = (...args: any[]) => {
    originalLog.apply(console, args);
    addLog('log', ...args);
};

console.warn = (...args: any[]) => {
    originalWarn.apply(console, args);
    addLog('warn', ...args);
};

console.error = (...args: any[]) => {
    originalError.apply(console, args);
    addLog('error', ...args);
};

export const getLogs = () => logs;

export const subscribeToLogs = (listener: (logs: LogEntry[]) => void) => {
    listeners.push(listener);
    return () => {
        listeners = listeners.filter(l => l !== listener);
    };
};

export const clearLogs = () => {
    logs = [];
    listeners.forEach(listener => listener(logs));
};
